import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiBaseUrl, getApiOrigin } from '../../config';

interface ChapterData {
  id: number;
  novel_id: number;
  novel_title: string;
  novel_cover?: string;
  volume_id: number;
  volume_name: string;
  volume_number?: number;
  chapter_number: number;
  title: string;
  content?: string;
  translator_note?: string;
  word_count: number;
  author: string;
  editor_admin_id?: number;
  editor_name?: string;
  chief_editor_admin_id?: number;
  chief_editor_name?: string;
  review_status: string;
  is_released: boolean;
  unlock_price: number;
  created_at: string;
  updated_at?: string;
  can_review?: boolean;
  requires_chief_edit?: boolean;
  // 章节导航信息（由后端计算）
  prev_chapter_id?: number | null;
  next_chapter_id?: number | null;
  total_chapters?: number;
  current_chapter_position?: number;
}

const ChapterReviewReader: React.FC = () => {
  const { chapterId } = useParams<{ chapterId: string }>();
  const navigate = useNavigate();
  
  // 状态
  const [chapter, setChapter] = useState<ChapterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // 字体大小
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('reviewReaderFontSize');
    return saved ? parseInt(saved, 10) : 18;
  });
  
  const MIN_FONT_SIZE = 14;
  const MAX_FONT_SIZE = 48;

  // API请求函数
  const adminApiRequest = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      throw new Error('未登录或登录已过期');
    }
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
    };
    
    if (!(options.body instanceof FormData) && !options.headers) {
      headers['Content-Type'] = 'application/json';
    }
    
    const base = getApiBaseUrl();
    if (!base) {
      throw new Error('API base url is not configured');
    }
    
    const response = await fetch(`${base}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 403) {
      throw new Error('Token无效或已过期，请重新登录');
    }

    const data = await response.json();

    if (!data.success && data.message && 
        (data.message.includes('Token') || data.message.includes('token') || 
         data.message.includes('登录') || data.message.includes('无效') || 
         data.message.includes('过期'))) {
      throw new Error(data.message || 'Token无效或已过期');
    }

    return { response, data };
  }, []);

  // 加载章节详情
  const loadChapter = useCallback(async () => {
    if (!chapterId) return;
    
    try {
      setLoading(true);
      setError(null);
      const { data } = await adminApiRequest(`/admin/chapter/${chapterId}`);
      
      if (data.success && data.data) {
        setChapter(data.data);
      } else {
        setError(data.message || '加载章节失败');
      }
    } catch (err: any) {
      setError(err.message || '加载章节失败');
    } finally {
      setLoading(false);
    }
  }, [chapterId, adminApiRequest]);

  // 处理审核
  const handleReview = async (result: string) => {
    if (!chapter) return;
    
    if (result === 'rejected' && !reviewComment.trim()) {
      showToast('拒绝时必须填写审核备注', 'error');
      return;
    }

    setSaving(true);
    try {
      const { data } = await adminApiRequest('/admin/chapter/review', {
        method: 'POST',
        body: JSON.stringify({
          chapter_id: chapter.id,
          result,
          comment: reviewComment
        })
      });
      
      if (data.success) {
        showToast(getReviewSuccessMessage(result), 'success');
        setReviewComment('');
        // 重新加载章节数据
        loadChapter();
      } else {
        showToast(data.message || '审核失败', 'error');
      }
    } catch (err: any) {
      showToast(err.message || '审核失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 获取审核成功消息
  const getReviewSuccessMessage = (result: string) => {
    switch (result) {
      case 'reviewing': return '已保存为审核中';
      case 'approved': return '审核通过成功';
      case 'rejected': return '审核拒绝成功';
      default: return '操作成功';
    }
  };

  // 显示提示
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 获取上一章/下一章ID（直接使用后端返回的导航信息）
  const getAdjacentChapters = () => {
    if (!chapter) {
      return { prevId: null, nextId: null, currentPosition: 0, total: 0 };
    }
    
    return {
      prevId: chapter.prev_chapter_id || null,
      nextId: chapter.next_chapter_id || null,
      currentPosition: chapter.current_chapter_position || 0,
      total: chapter.total_chapters || 0
    };
  };

  // 导航到其他章节
  const navigateToChapter = (id: number) => {
    navigate(`/admin/chapter-review/${id}`);
    window.scrollTo(0, 0);
  };

  // 处理字体大小变化
  const handleFontSizeChange = (delta: number) => {
    setFontSize(prev => {
      const newSize = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, prev + delta));
      localStorage.setItem('reviewReaderFontSize', newSize.toString());
      return newSize;
    });
  };

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return;
      }
      
      const { prevId, nextId } = getAdjacentChapters();
      
      if (e.key === 'ArrowLeft' && prevId) {
        navigateToChapter(prevId);
      } else if (e.key === 'ArrowRight' && nextId) {
        navigateToChapter(nextId);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [chapter]);

  // 初始加载
  useEffect(() => {
    loadChapter();
  }, [loadChapter]);

  // 获取状态文本
  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'submitted': '已提交',
      'reviewing': '审核中',
      'approved': '已批准',
      'rejected': '已拒绝',
      'draft': '草稿',
      'pending_chief': '等待主编终审'
    };
    return statusMap[status] || status;
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'submitted': '#f59e0b',
      'reviewing': '#3b82f6',
      'approved': '#10b981',
      'rejected': '#ef4444',
      'draft': '#6b7280',
      'pending_chief': '#f59e0b'
    };
    return colorMap[status] || '#6b7280';
  };

  // 获取封面URL
  const getCoverUrl = (cover?: string) => {
    if (!cover) return '/default-cover.jpg';
    if (cover.startsWith('http')) return cover;
    if (cover.startsWith('/')) return `${getApiOrigin()}${cover}`;
    return `${getApiOrigin()}/covers/${cover}`;
  };

  // 格式化内容
  const formatContent = (content: string) => {
    if (!content) return null;
    
    return content.split('\n').map((paragraph, idx) => {
      if (!paragraph.trim()) {
        return <div key={idx} style={{ height: '1em' }} />;
      }
      return (
        <p key={idx} style={{ 
          margin: '0 0 1em 0',
          textIndent: '2em',
          lineHeight: 1.8
        }}>
          {paragraph}
        </p>
      );
    });
  };

  const { prevId, nextId, currentPosition, total } = getAdjacentChapters();
  const canReview = chapter?.can_review === true;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '3px solid rgba(99, 102, 241, 0.3)',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>加载章节内容...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !chapter) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <span style={{ fontSize: 32 }}>!</span>
          </div>
          <h2 style={{ marginBottom: 8 }}>加载失败</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>{error || '章节不存在'}</p>
          <button
            onClick={() => navigate('/admin')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            返回后台
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      color: 'white',
      paddingBottom: 200 // 为底部审核区域留出空间
    }}>
      {/* Toast 提示 */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '12px 24px',
          borderRadius: 8,
          background: toast.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          fontWeight: 600,
          zIndex: 1000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          animation: 'slideDown 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* 顶部导航栏 */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(26, 26, 46, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        padding: '12px 24px'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16
        }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: 8,
              color: '#a5b4fc',
              cursor: 'pointer',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = 'rgba(99, 102, 241, 0.3)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
            }}
          >
            <span>←</span>
            <span>返回章节审批</span>
          </button>
          
          <div style={{
            flex: 1,
            textAlign: 'center',
            overflow: 'hidden'
          }}>
            <h1 style={{
              fontSize: 16,
              fontWeight: 600,
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {chapter.novel_title} - 第{chapter.chapter_number}章: {chapter.title}
            </h1>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => handleFontSizeChange(-2)}
              disabled={fontSize <= MIN_FONT_SIZE}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                cursor: fontSize <= MIN_FONT_SIZE ? 'not-allowed' : 'pointer',
                opacity: fontSize <= MIN_FONT_SIZE ? 0.5 : 1,
                fontSize: 18,
                fontWeight: 700
              }}
            >
              A-
            </button>
            <span style={{ 
              fontSize: 14, 
              color: 'rgba(255,255,255,0.7)',
              minWidth: 40,
              textAlign: 'center'
            }}>
              {fontSize}px
            </span>
            <button
              onClick={() => handleFontSizeChange(2)}
              disabled={fontSize >= MAX_FONT_SIZE}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                cursor: fontSize >= MAX_FONT_SIZE ? 'not-allowed' : 'pointer',
                opacity: fontSize >= MAX_FONT_SIZE ? 0.5 : 1,
                fontSize: 18,
                fontWeight: 700
              }}
            >
              A+
            </button>
          </div>
        </div>
      </header>

      {/* 主内容区域 */}
      <main style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: '24px 20px'
      }}>
        {/* 章节信息卡片 */}
        <div style={{
          display: 'flex',
          gap: 20,
          padding: 20,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 16,
          border: '1px solid rgba(255,255,255,0.1)',
          marginBottom: 24
        }}>
          {chapter.novel_cover && (
            <img
              src={getCoverUrl(chapter.novel_cover)}
              alt={chapter.novel_title}
              style={{
                width: 80,
                height: 110,
                objectFit: 'cover',
                borderRadius: 8,
                flexShrink: 0
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ 
              fontSize: 20, 
              fontWeight: 700, 
              margin: '0 0 12px 0',
              color: 'white'
            }}>
              {chapter.novel_title}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '8px 16px',
              fontSize: 14,
              color: 'rgba(255,255,255,0.7)'
            }}>
              <span><strong style={{ color: 'rgba(255,255,255,0.5)' }}>章节号：</strong>{chapter.chapter_number}</span>
              <span><strong style={{ color: 'rgba(255,255,255,0.5)' }}>章节ID：</strong>{chapter.id}</span>
              <span><strong style={{ color: 'rgba(255,255,255,0.5)' }}>字数：</strong>{chapter.word_count}</span>
              <span><strong style={{ color: 'rgba(255,255,255,0.5)' }}>作者：</strong>{chapter.author}</span>
              <span>
                <strong style={{ color: 'rgba(255,255,255,0.5)' }}>审核状态：</strong>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: `${getStatusColor(chapter.review_status)}20`,
                  color: getStatusColor(chapter.review_status),
                  fontWeight: 600,
                  fontSize: 12
                }}>
                  {getStatusText(chapter.review_status)}
                </span>
              </span>
              <span><strong style={{ color: 'rgba(255,255,255,0.5)' }}>责任编辑：</strong>{chapter.editor_name || '未分配'}</span>
            </div>
          </div>
        </div>

        {/* 正文内容 */}
        <article style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: 16,
          padding: '32px 40px',
          fontSize: fontSize,
          lineHeight: 1.8,
          color: 'rgba(255,255,255,0.9)'
        }}>
          {chapter.content ? (
            formatContent(chapter.content)
          ) : (
            <p style={{ 
              color: 'rgba(255,255,255,0.5)', 
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              暂无内容
            </p>
          )}
        </article>

        {/* 作者有话说 */}
        {chapter.translator_note && chapter.translator_note.trim() && (
          <div style={{
            marginTop: 24,
            padding: '20px 24px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
            borderRadius: 12,
            borderLeft: '4px solid #6366f1'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12,
              color: '#a5b4fc',
              fontWeight: 600,
              fontSize: 15
            }}>
              <span style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700
              }}>宣</span>
              <span>作者有话说 / Author's Note</span>
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: 15,
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap'
            }}>
              {chapter.translator_note}
            </div>
          </div>
        )}
      </main>

      {/* 固定底部审核操作区 */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(26, 26, 46, 0.98)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '16px 24px',
        zIndex: 100
      }}>
        <div style={{
          maxWidth: 900,
          margin: '0 auto'
        }}>
          {/* 章节导航 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            paddingBottom: 16,
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <button
              onClick={() => prevId && navigateToChapter(prevId)}
              disabled={!prevId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                background: prevId ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255,255,255,0.05)',
                border: '1px solid ' + (prevId ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255,255,255,0.1)'),
                borderRadius: 8,
                color: prevId ? '#a5b4fc' : 'rgba(255,255,255,0.3)',
                cursor: prevId ? 'pointer' : 'not-allowed',
                fontWeight: 500,
                transition: 'all 0.2s'
              }}
            >
              ← 上一章
            </button>
            
            <span style={{ 
              color: 'rgba(255,255,255,0.6)',
              fontSize: 14
            }}>
              第 {currentPosition} 章 / 共 {total} 章
            </span>
            
            <button
              onClick={() => nextId && navigateToChapter(nextId)}
              disabled={!nextId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                background: nextId ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255,255,255,0.05)',
                border: '1px solid ' + (nextId ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255,255,255,0.1)'),
                borderRadius: 8,
                color: nextId ? '#a5b4fc' : 'rgba(255,255,255,0.3)',
                cursor: nextId ? 'pointer' : 'not-allowed',
                fontWeight: 500,
                transition: 'all 0.2s'
              }}
            >
              下一章 →
            </button>
          </div>

          {/* 审核操作 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ 
                color: 'rgba(255,255,255,0.7)',
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}>
                审核备注：
              </label>
              <input
                type="text"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="请输入审核备注（审核拒绝时必填）..."
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  outline: 'none'
                }}
              />
            </div>
            
            {!canReview && (
              <div style={{
                padding: '10px 16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: 8,
                color: '#fca5a5',
                fontSize: 13
              }}>
                您当前对本章节没有审核权限（可能是没有生效合同，或章节状态/归属不允许）
              </div>
            )}
            
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => handleReview('reviewing')}
                disabled={saving || !canReview}
                style={{
                  padding: '12px 24px',
                  background: canReview ? '#3b82f6' : 'rgba(59, 130, 246, 0.3)',
                  border: 'none',
                  borderRadius: 8,
                  color: 'white',
                  fontWeight: 600,
                  cursor: canReview && !saving ? 'pointer' : 'not-allowed',
                  opacity: canReview && !saving ? 1 : 0.5,
                  transition: 'all 0.2s'
                }}
              >
                保存为"审核中"
              </button>
              <button
                onClick={() => handleReview('approved')}
                disabled={saving || !canReview}
                style={{
                  padding: '12px 24px',
                  background: canReview ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'rgba(16, 185, 129, 0.3)',
                  border: 'none',
                  borderRadius: 8,
                  color: 'white',
                  fontWeight: 600,
                  cursor: canReview && !saving ? 'pointer' : 'not-allowed',
                  opacity: canReview && !saving ? 1 : 0.5,
                  transition: 'all 0.2s',
                  boxShadow: canReview ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none'
                }}
              >
                审核通过
              </button>
              <button
                onClick={() => handleReview('rejected')}
                disabled={saving || !canReview || !reviewComment.trim()}
                style={{
                  padding: '12px 24px',
                  background: (canReview && reviewComment.trim()) ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'rgba(239, 68, 68, 0.3)',
                  border: 'none',
                  borderRadius: 8,
                  color: 'white',
                  fontWeight: 600,
                  cursor: (canReview && !saving && reviewComment.trim()) ? 'pointer' : 'not-allowed',
                  opacity: (canReview && !saving && reviewComment.trim()) ? 1 : 0.5,
                  transition: 'all 0.2s'
                }}
              >
                审核拒绝
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default ChapterReviewReader;
